// Phase 8.6 follow-up — the personnel create/edit page (wraps the
// EmployeeFormContent). Mirrors leave-file-page.tsx: the host that wires the
// shell-agnostic EmployeeFormContent's `onSubmit` to the real write path —
// POST /v1/employees (create) or PATCH /v1/employees/:id (update). The route
// param `:id` selects the mode; edit fetches the existing row to prefill.
//
// On success the page invalidates the employees queries so every surface
// reflects the new/updated row. An EDIT returns to that person's card (the page
// the user came from; E10 — landing on the list made them find the person
// again), a CREATE goes to the list.

import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import { Alert, Loading, ErrorState } from "@novemai/components";

import { EnvelopeError, errorMessage } from "@/api/errors";
import { fetchCompanies } from "@/features/admin-settings/companies-api";
import { DEPARTMENTS_QUERY_KEY, fetchDepartments } from "@/features/departments/api";
import { dimensionValueOptions, fetchOrgDimensions } from "@/features/org-dimensions/api";
import {
  EmployeeFormContent,
  type EmployeeDraft,
  type OrgDimensionField,
} from "@/content/employee-form-content";

import {
  createEmployee,
  fetchEmployee,
  fetchEmployeesFirstPage,
  updateEmployee,
  type CreateEmployeeInput,
  type UpdateEmployeeInput,
} from "./api";

interface PendingEmployeeMutation {
  readonly fingerprint: string;
  readonly payload: UpdateEmployeeInput;
  readonly idempotencyKey: string;
}

function toEmployeePayload(
  draft: EmployeeDraft,
  mode: "create" | "edit",
): UpdateEmployeeInput {
  return {
    // Boyut değerleri YALNIZ yamada: yaratma ucu alanı ilan etmiyor (SEM-7205).
    ...(mode === "edit" && { org_dimension_values: draft.org_dimension_values }),
    legal_first_name: draft.legal_first_name,
    legal_last_name: draft.legal_last_name,
    primary_work_email: draft.primary_work_email,
    start_date: draft.start_date,
    ...(draft.job_title !== "" && { job_title: draft.job_title }),
    ...(draft.employment_type !== "" && { employment_type: draft.employment_type }),
    ...(draft.manager_id !== "" && { manager_id: draft.manager_id }),
    ...(draft.department_id !== "" && { department_id: draft.department_id }),
  };
}

function employeeMutationFingerprint(
  mode: "create" | "edit",
  editId: string | null,
  payload: UpdateEmployeeInput,
  draftCompanyId: string,
): string {
  return JSON.stringify([
    mode,
    editId,
    payload.legal_first_name,
    payload.legal_last_name,
    payload.primary_work_email,
    payload.start_date,
    payload.job_title ?? null,
    payload.employment_type ?? null,
    payload.manager_id ?? null,
    payload.department_id ?? null,
    payload.org_dimension_values ?? null,
    // Şirket parmak izinde kalıyor: yalnız yaratma yolunda gövdeye giriyor ama
    // ORADA gövdenin parçası. Parmak izinden düşürmek, yalnız şirketi
    // değiştirilmiş bir yaratma isteğini "aynı istek" sayıp ilk cevabı geri
    // oynatırdı.
    draftCompanyId,
  ]);
}

function isDefinitiveEmployeeFailure(error: unknown): boolean {
  return error instanceof EnvelopeError && error.httpStatus >= 400 && error.httpStatus < 500;
}

export function EmployeeFormPage(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams();
  const editId = params["id"] ?? null;
  const mode: "create" | "edit" = editId !== null ? "edit" : "create";

  // Retain the exact normalized payload + key only while the outcome is
  // ambiguous. An unchanged retry can then replay server-side; an edited form
  // is intentionally a new logical write.
  const pendingMutation = useRef<PendingEmployeeMutation | null>(null);

  // Edit-mode prefill — fetch the existing row (gates the form render).
  const existing = useQuery({
    queryKey: ["employees", "detail", editId],
    queryFn: () => fetchEmployee(editId ?? ""),
    enabled: editId !== null,
  });

  // Manager picker options — the first page of the employees list (the only
  // ready FE option-source). A bounded one-shot read (no pagination — a picker
  // option set, not a scrollable list). In edit mode the employee can't be
  // their own manager, so exclude their own id.
  const managers = useQuery({
    queryKey: ["employees", "manager-options"],
    queryFn: fetchEmployeesFirstPage,
  });
  const managerOptions = (managers.data?.items ?? [])
    .filter((row) => row.employee_id !== editId)
    .map((row) => ({
      value: row.employee_id,
      label: `${row.legal_first_name} ${row.legal_last_name}`,
    }));

  // Şirket seçicisinin seçenekleri (SEM-6755). Uç çağıranın hibesine
  // daraltılmış: dar yetkili bir yönetici burada yalnız kendi şirketlerini
  // görür, yani yazma ucunun reddedeceği bir seçenek hiç çizilmez.
  const companies = useQuery({
    queryKey: ["legal-entities", "picker", "active"],
    queryFn: () => fetchCompanies("active"),
  });
  const companyOptions = (companies.data ?? []).map((row) => ({
    value: row.legal_entity_id,
    label: row.name,
  }));

  // Departman seçicisi (E10): aktif departmanlar, tek okuma. Okunamazsa alan
  // çizilmez ve kayıt yine de yapılır (departman seçici bir isteğe bağlı alandır,
  // şirket gibi yazma ucunun zorunlu koşulu değil).
  const departments = useQuery({
    queryKey: [...DEPARTMENTS_QUERY_KEY, "form-picker", "active"],
    queryFn: () => fetchDepartments({ lifecycle_state: "active", limit: 500 }),
  });
  const departmentOptions = (departments.data ?? []).map((row) => ({
    value: row.department_id,
    label: row.name,
  }));

  // Aktif boyut kataloğu (SEM-7205). Yalnız düzenlemede okunuyor: yaratma
  // yolunda alan hiç çizilmiyor, o yüzden orada bir de istek yapmak boşuna
  // olurdu. Arşivlenmiş boyut listede yok — uç de onu reddediyor.
  const dimensions = useQuery({
    queryKey: ["org-dimensions", "employee-form"],
    queryFn: () => fetchOrgDimensions({ state: "active" }),
    enabled: mode === "edit",
  });
  const dimensionFields: readonly OrgDimensionField[] = (dimensions.data ?? []).map((row) => {
    // hierarchy_small'ın izin verilen değerleri bir AĞAÇ; uç değeri o ağacın
    // herhangi bir düğümüne karşı ölçüyor. Düz metin alanı bırakmak, kullanıcıya
    // yasal kümeyi hiç göstermeden 400 aldırırdı — ağaç düğüm değerlerine
    // düzleniyor ve seçici olarak çiziliyor.
    const allowed = dimensionValueOptions(row.allowed_values);
    return {
      slug: row.slug,
      label: row.name,
      ...(allowed.length > 0 && { allowedValues: allowed }),
    };
  });

  const mutation = useMutation({
    mutationFn: async (draft: EmployeeDraft) => {
      // Drop empty optionals so the backend keeps them unset (PATCH) / defaults
      // them (POST). The 4 required fields always ride. The payload is
      // byte-identical for both verbs (UpdateEmployeeInput = Partial<Create…>),
      // so build it once and only the call differs.
      const payload = toEmployeePayload(draft, mode);
      const fingerprint = employeeMutationFingerprint(mode, editId, payload, draft.legal_entity_id);
      const intent =
        pendingMutation.current?.fingerprint === fingerprint
          ? pendingMutation.current
          : {
              fingerprint,
              payload,
              idempotencyKey: crypto.randomUUID(),
            };
      pendingMutation.current = intent;
      return mode === "edit" && editId !== null
        ? updateEmployee(editId, intent.payload, intent.idempotencyKey)
        : // Ünvan işe alımda zorunlu (MC-19a) ve form doğrulayıcısı boş
          // bırakılmasına izin vermiyor; yama gövdesi ise ünvanı hiç
          // taşımayabilir, bu yüzden yalnız yaratma yolu onu ilan ediyor.
          // Şirket aynı sınıfta ama ters sebeple: yama ucu artık onu
          // REDDEDİYOR (MC-19b), transfer ayrı bir geçiş.
          createEmployee(
            {
              ...intent.payload,
              job_title: draft.job_title,
              ...(draft.legal_entity_id !== "" && { legal_entity_id: draft.legal_entity_id }),
            } as CreateEmployeeInput,
            intent.idempotencyKey,
          );
    },
    onSuccess: () => {
      pendingMutation.current = null;
      void queryClient.invalidateQueries({ queryKey: ["employees"] });
      // Frontend-audit MEDIUM-7: an edit can change manager_id, which the
      // org-chart's own separate query key (["org","chart","live"]) never
      // shared an invalidation path with — the chart stayed stale until an
      // unrelated refetch/route change.
      void queryClient.invalidateQueries({ queryKey: ["org", "chart", "live"] });
      void navigate(mode === "edit" && editId !== null ? `/people/${editId}` : "/people");
    },
    onError: (error) => {
      if (isDefinitiveEmployeeFailure(error)) pendingMutation.current = null;
    },
  });

  // Both modes wait on the manager options before rendering: the form's
  // manager field is an UNCONTROLLED native <select> that takes its saved value
  // via `defaultValue`. If it mounts before `managers` resolves, the matching
  // <option> doesn't exist yet and the browser drops the defaultValue — losing
  // the saved manager on edit. Gating here guarantees the <select> mounts once
  // with the full option set so `defaultValue` lands on a real <option>.
  //
  // Şirket seçicisi aynı sınıftan: o da kontrolsüz bir <select>. Bekleme
  // listesine katılmazsa kayıtlı şirket düzenlemede sessizce düşerdi — ve
  // düşen değer PATCH'e boş gitmez, hiç gitmez, yani kusur ancak çok
  // şirketli kiracıda ve yalnız ekranda görünürdü.
  //
  // Departman seçicisi de aynı sınıftan: kontrolsüz bir <select>, bu yüzden
  // seçenekler gelmeden çizilirse kayıtlı departman düşerdi.
  if (
    managers.isLoading ||
    companies.isLoading ||
    departments.isLoading ||
    (mode === "edit" && (existing.isLoading || dimensions.isLoading))
  ) {
    return (
      <div className="mx-auto max-w-[760px]">
        <Loading label={t("people.loading")} rows={5} />
      </div>
    );
  }
  // `dimensions.isError` de hata dalı: katalog okunamadığında form boyut alanı
  // çizmez ve bir kayıt, kullanıcı hiçbir şey görmeden aktif boyut değerlerini
  // kaybederdi (harita bütün olarak yamalanıyor).
  if (
    mode === "edit" &&
    (existing.isError || existing.data === undefined || dimensions.isError)
  ) {
    return (
      <div className="mx-auto max-w-[760px]">
        <ErrorState
          title={t("people.error_title")}
          description={errorMessage(existing.error ?? dimensions.error, t)}
        />
      </div>
    );
  }

  const row = existing.data;
  const defaults: Partial<EmployeeDraft> | undefined =
    mode === "edit" && row !== undefined
      ? {
          legal_first_name: row.legal_first_name,
          legal_last_name: row.legal_last_name,
          primary_work_email: row.primary_work_email,
          start_date: row.start_date ?? "",
          job_title: row.job_title ?? "",
          employment_type: row.employment_type ?? "",
          manager_id: row.manager_id ?? "",
          department_id: row.department_id ?? "",
          legal_entity_id: row.legal_entity_id ?? "",
          org_dimension_values: row.org_dimension_values ?? {},
        }
      : undefined;

  // Kişinin mevcut departmanı arşivlenmişse aktif listede yok; seçenek
  // eksik kalırsa kontrolsüz <select> kayıtlı değeri sessizce düşürürdü.
  const saved =
    mode === "edit" && row !== undefined && row.department_id !== null && row.department_name !== null
      ? { value: row.department_id, label: row.department_name }
      : null;
  const departmentPickerOptions =
    saved !== null && !departmentOptions.some((option) => option.value === saved.value)
      ? [...departmentOptions, saved]
      : departmentOptions;

  return (
    <div className="space-y-5">
      {mutation.isError && (
        <div className="mx-auto max-w-[760px]">
          <Alert severity="danger" title={t("people.form.error_title")}>
            {errorMessage(mutation.error, t)}
          </Alert>
        </div>
      )}
      <EmployeeFormContent
        mode={mode}
        managerOptions={managerOptions}
        departmentOptions={departmentPickerOptions}
        companyOptions={companyOptions}
        dimensions={dimensionFields}
        {...(defaults !== undefined && { defaults })}
        onSubmit={(draft): void => {
          mutation.mutate(draft);
        }}
      />
    </div>
  );
}
